import { useState, useEffect } from "react";
import { Box, Typography, Button, Alert } from "@mui/material";
import { Zap, Check, ArrowRight } from "lucide-react";
import FlexBox from "components/common/FlexBox";
import { useCreateDelegationTask } from "services/delegation";
import { CreateDelegationTaskRequest } from "protogen/delegation_service_pb";
import {
  DelegationOpportunity,
  DelegationOpportunityGroup,
} from "components/hooks/useTaskAgentState";
import useIsMobile from "components/hooks/useIsMobile";
import DelegationConfirmDialog, {
  DelegationDialogData,
} from "components/delegation/DelegationConfirmDialog";
import { LIGHTNING_TASK_TYPE_PHONE_CALLS } from "types/delegation";

const getStatusText = (status: string) => {
  switch (status) {
    case "completed":
      return "Completed";
    case "in_progress":
    case "delegated":
      return "In progress";
    case "queued":
      return "Queued";
    case "cancelled":
      return "Cancelled";
    default:
      return "Pending";
  }
};

const SimpleDelegationEntry = ({ task }: { task: DelegationOpportunity }) => {
  const isMobile = useIsMobile();
  const handleClick = () => {
    const blockElement = document.getElementById(`delegation-${task.ref}`);
    if (blockElement) {
      blockElement.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  return (
    <Box
      onClick={handleClick}
      sx={{
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "space-between",
        alignItems: "center",
        gap: isMobile ? "8px" : "16px",
        padding: "12px 16px",
        backgroundColor: "#FAF9FA",
        border: "1px solid rgba(0, 0, 0, 0.1)",
        borderRadius: "8px",
        cursor: "pointer",
        "&:hover": { backgroundColor: "#F5F4F5" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <Zap size={18} color="#198282" fill="#198282" />
        <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
          <FlexBox alignItems="center" gap="6px">
            <Typography
              variant="caption"
              sx={{
                color: "#198282",
                fontSize: "12px",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              LIGHTNING TASK
            </Typography>
          </FlexBox>
          <Typography variant="bodySmall" color="text.secondary">
            {getStatusText(task.status)}
          </Typography>
        </Box>
      </Box>
      <Button
        variant="text"
        sx={{
          color: "#198282",
          fontWeight: 500,
          justifyContent: "flex-start",
          height: "unset",
        }}
        startIcon={<Check size={18} color="#198282" />}
        onClick={(e) => {
          e.preventDefault();
        }}
      >
        Lightning task created
      </Button>
    </Box>
  );
};

const DelegationEntry = ({
  task,
  onDelegate,
  taskRef,
  familyRef,
  isLoading = false,
  isSubmitted = false,
  showActions = true,
}: {
  task: DelegationOpportunity;
  onDelegate: (data: DelegationDialogData) => Promise<boolean>;
  taskRef?: string;
  familyRef?: string;
  isLoading?: boolean;
  isSubmitted?: boolean;
  showActions?: boolean;
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);

  const hasTask = isSubmitted || task.status !== "pending";

  if (hasTask) {
    return <SimpleDelegationEntry task={task} />;
  }

  const handleSendClick = () => {
    setDialogOpen(true);
  };

  const handleConfirm = async (data: DelegationDialogData) => {
    const success = await onDelegate(data);
    if (success) {
      setDialogOpen(false);
    }
  };

  return (
    <>
      <FlexBox
        variant="contained"
        sx={{
          backgroundColor: "#FAF9FA",
          borderColor: "rgba(0, 0, 0, 0.1)",
          flexWrap: "wrap",
        }}
        justifyContent="space-between"
        alignItems="center"
        gap="16px"
      >
        <FlexBox direction="column" gap="4px" sx={{ flex: 1, minWidth: 200 }}>
          <FlexBox alignItems="center" gap="6px">
            <Zap size={14} color="#198282" fill="#198282" />
            <Typography
              variant="caption"
              sx={{
                color: "#198282",
                fontSize: "12px",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              LIGHTNING TASK
            </Typography>
          </FlexBox>
          <Typography variant="bodyHeavy">{task.title}</Typography>
          <Typography variant="bodySmall" color="text.secondary">
            Lightning tasks are routed to the next available specialist. We
            provide only the details needed to get it done fast.
          </Typography>
        </FlexBox>
        {showActions && (
          <Button
            variant="contained"
            onClick={handleSendClick}
            endIcon={<ArrowRight size={16} />}
            sx={{
              flexGrow: { xs: 1, sm: 0 },
              flexShrink: 0,
              width: { xs: "100%", sm: "auto" },
            }}
          >
            Confirm details
          </Button>
        )}
      </FlexBox>

      <DelegationConfirmDialog
        open={dialogOpen}
        task={task}
        taskRef={taskRef}
        familyRef={familyRef}
        onClose={() => setDialogOpen(false)}
        onConfirm={handleConfirm}
        isLoading={isLoading}
      />
    </>
  );
};

export const DelegationsSection = ({
  delegationOpportunityGroups,
  agentThreadRef,
  taskRef,
  familyRef,
  onDelegate,
  refreshTask,
  showActions = true,
}: {
  delegationOpportunityGroups: DelegationOpportunityGroup[];
  agentThreadRef?: string;
  taskRef?: string;
  familyRef?: string;
  onDelegate?: (opportunityId: string) => void;
  refreshTask?: () => Promise<void>;
  showActions?: boolean;
}) => {
  const [loadingOpportunities, setLoadingOpportunities] = useState<Set<string>>(
    new Set(),
  );
  const [submittedOpportunities, setSubmittedOpportunities] = useState<
    Set<string>
  >(() => {
    const submitted = new Set<string>();
    delegationOpportunityGroups.forEach((group) => {
      group.opportunities.forEach((opp) => {
        if (opp.ref || opp.status !== "pending") {
          submitted.add(opp.functionId);
        }
      });
    });
    return submitted;
  });
  const [error, setError] = useState<string | null>(null);
  const { request, loading } = useCreateDelegationTask();

  useEffect(() => {
    setSubmittedOpportunities((prev) => {
      const updated = new Set(prev);
      delegationOpportunityGroups.forEach((group) => {
        group.opportunities.forEach((opp) => {
          if (opp.ref || opp.status !== "pending") {
            updated.add(opp.functionId);
          }
        });
      });
      return updated;
    });
  }, [delegationOpportunityGroups]);

  const handleDelegate = async (
    opportunity: DelegationOpportunity,
    data: DelegationDialogData,
  ): Promise<boolean> => {
    setLoadingOpportunities((prev) =>
      new Set(prev).add(opportunity.functionId),
    );
    setError(null);

    // Protobuf int64 fields (actionDateSec) expect bigint in TypeScript, but BigInt cannot be
    // JSON serialized. We use number instead, which works at runtime. Type assertion needed.
    const result = await request({
      title: opportunity.title,
      description: data.description,
      detailsJson: JSON.stringify(data.details),
      agentThreadRef: opportunity.agentThreadRef || agentThreadRef || "",
      taskRef: taskRef || "",
      functionCallId: opportunity.functionId,
      serviceType: opportunity.serviceType || LIGHTNING_TASK_TYPE_PHONE_CALLS,
      actionDateSec: opportunity.actionDateSec || 0,
      timeEstimateMinutes: data.timeEstimateMinutes || 0,
    } as unknown as CreateDelegationTaskRequest);

    setLoadingOpportunities((prev) => {
      const next = new Set(prev);
      next.delete(opportunity.functionId);
      return next;
    });

    if (result) {
      setSubmittedOpportunities((prev) =>
        new Set(prev).add(opportunity.functionId),
      );
      onDelegate?.(opportunity.functionId);
      refreshTask?.();
    } else {
      setError("Failed to create delegation task");
      return false;
    }
    return true;
  };

  if (delegationOpportunityGroups.length === 0) return null;

  return (
    <FlexBox direction="column" gap="16px">
      {delegationOpportunityGroups.map((group) => (
        <FlexBox key={group.id} direction="column" gap="16px">
          {group.opportunities.map((opportunity) => (
            <DelegationEntry
              key={opportunity.functionId}
              task={opportunity}
              taskRef={taskRef}
              familyRef={familyRef}
              onDelegate={async (data) =>
                await handleDelegate(opportunity, data)
              }
              isLoading={
                loadingOpportunities.has(opportunity.functionId) || loading
              }
              isSubmitted={submittedOpportunities.has(opportunity.functionId)}
              showActions={showActions}
            />
          ))}

          {error && <Alert severity="error">{error}</Alert>}
        </FlexBox>
      ))}
    </FlexBox>
  );
};
